/* eslint-disable no-case-declarations */
/* eslint-disable no-fallthrough */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CstNode, ILexingError, IRecognitionException, IToken, Lexer } from 'chevrotain';
import { CompoundedParser } from './CompoundedParser';
import { GregorioLiquescentia } from '../../enums/GregorioLiquescentia';
import { GregorioShape, punctumInclinatum } from '../../enums/GregorioShape';
import { GregorioSign } from '../../enums/GregorioSign';
import { GregorioNoteElement, addHorizontalEpisemaFromText } from '../../structures/GregorioNoteElement';
import { GregorioSyllable, GregorioWordPosition } from '../../structures/GregorioSyllable';
import { GregorioClefInfo } from '../../structures/GregorioClef';
import { NabcSnippet } from '../../structures/NabcSnippet';
import { NabcNeume } from '../../structures/NabcNeume';
import { NabcGlyph } from '../../structures/NabcGlyph';
import { NabcSignificantLetter } from '../../structures/NabcSignificantLetter';
import { NabcSubpunctisAndPrepunctis } from '../../structures/NabcSubpunctisAndPrepunctis';
import { GregorioSpace } from '../../enums/GregorioSpace';
import { GregorioBar } from '../../enums/GregorioBar';
import { GregorioSegment } from '../../structures/GregorioSegment';
import { TextSpan, TextSpanType } from '../../structures/TextSpan';
import { GregorioMetadata } from '../../structures/GregorioMetadata';
import { split_metadata } from '../../utils/cursor_positions';
import { cloneDeep, flatten, sortBy } from 'lodash';


function findBar(context: any) {
  const barKeys = Object.keys(context).filter((key) => key.startsWith('B_'));

  if (barKeys.length === 0) {
    return null;
  }

  return context[barKeys[0]][0];
}

export class InterpreterContext {
  nbof_isolated_episema = 0;
}

let spantype = TextSpanType.Normal;

export function CompoundedInterpreterFactory(parser: CompoundedParser) {
  const BaseCstVisitor = parser.getBaseCstVisitorConstructor();

  class CompoundedInterpreter extends BaseCstVisitor {
    constructor() {
      super();
      this.validateVisitor();
    }

    public score(context: any): GregorioSyllable[] {
      if (!context.word) {
        return [];
      }
      const syllables = context.word.map((word: CstNode) => this.visit(word)) as GregorioSyllable[];
      return syllables.flat();
    }

    public word(context: any): GregorioSyllable[] {
      if (!context.syllable) {
        return [];
      }

      const syllables = context.syllable.map((syllable: CstNode) => this.visit(syllable)) as GregorioSyllable[];
      const leftover: TextSpan[] = this.visit(context.lyrics) || [];

      if (syllables.length === 1 && leftover.length === 0) {
        syllables[0].position = GregorioWordPosition.WORD_ONE_SYLLABLE;
      } else {
        syllables[0].position = GregorioWordPosition.BEGINNING;
        for (let i = 1; i < (leftover.length > 0 ? syllables.length : syllables.length - 1); i++) {
          syllables[i].position = GregorioWordPosition.WORD_MIDDLE;
        }

        if (leftover.length > 0) {
          const lastSyllable = new GregorioSyllable();
          lastSyllable.textSpans = leftover;
          lastSyllable.position = GregorioWordPosition.WORD_END;
          syllables.push(lastSyllable);
        } else {
          syllables[syllables.length - 1].position = GregorioWordPosition.WORD_END;
        }
      }

      return syllables;
    }

    public syllable(context: any): GregorioSyllable {
      const syllable = new GregorioSyllable();
      syllable.textSpans = flatten(context.lyrics?.map((l: CstNode) => this.visit(l))) || [];
      syllable.keepWithNext = (spantype & TextSpanType.Euouae) > 0 || (spantype & TextSpanType.NonBreaking) > 0;
      const normalSegments = context.segment?.map((segment: CstNode) => this.visit(segment)) || [];
      const nabcOnlySegments = context.nabcOnlySegment?.map((segment: CstNode) => this.visit(segment)) || [];
      syllable.segments = [...normalSegments, ...nabcOnlySegments];
      return syllable;
    }

    public lyrics(context: any): TextSpan[] {
      return this._lyricsInternal(context, spantype);
    }

    public _lyricsInternal(context: any, parentType: TextSpanType): TextSpan[] {
      spantype = parentType;
      const elements = Object.keys(context).map((key) => context[key]).flat();
      const orderedElements = sortBy(elements, _ => this._findMinStartOffset(_));
      const arr: TextSpan[] = [];

      for (const element of orderedElements) {
        if (element.name === 'lyrics') {
          const children = this._lyricsInternal(element.children, spantype);
          arr.push(...children);
        } else if (element.tokenType.name === 'STRING_LITERAL' || element.tokenType.name === 'WHITESPACE_SINGLE') {
          arr.push(new TextSpan(element.image, spantype));
        } else if (element.tokenType.name === 'STRING_LITERAL_OPENING_TAG') {
          switch (element.image) {
            case '<b>':
              spantype |= TextSpanType.Bold;
              break;
            case '<i>':
              spantype |= TextSpanType.Italic;
              break;
            case '<sp>':
              spantype |= TextSpanType.Special;
              break;
            case '<v>':
              spantype |= TextSpanType.Verbatim;
              break;
            case '<c>':
              spantype |= TextSpanType.Colored;
              break;
            case '<eu>':
              spantype |= TextSpanType.Euouae;
              break;
            case '<nlba>':
              spantype |= TextSpanType.NonBreaking;
              break;
            case '<alt>':
              spantype |= TextSpanType.AboveLinesText;
              break;
          }
        } else if (element.tokenType.name === 'STRING_LITERAL_CLOSING_TAG') {
          switch (element.image) {
            case '</b>':
              spantype &= ~TextSpanType.Bold;
              break;
            case '</i>':
              spantype &= ~TextSpanType.Italic;
              break;
            case '</sp>':
              spantype &= ~TextSpanType.Special;
              break;
            case '</v>':
              spantype &= ~TextSpanType.Verbatim;
              break;
            case '</c>':
              spantype &= ~TextSpanType.Colored;
              break;
            case '</eu>':
              spantype &= ~TextSpanType.Euouae;
              break;
            case '</nlba>':
              spantype &= ~TextSpanType.NonBreaking;
              break;
            case '</alt>':
              spantype &= ~TextSpanType.AboveLinesText;
              break;
          }
        }
      }

      return arr;
    }

    public _findMinStartOffset(context: any): number {
      if (context.startOffset !== undefined) {
        return context.startOffset;
      }
      const flatChildren = Object.keys(context.children).map((key: string) => context.children[key]).flat();
      const ordered = sortBy(flatChildren, _ => this._findMinStartOffset(_));
      return ordered.length > 0 ? ordered[0].startOffset : 0;
    }

    public segment(context: any): GregorioSegment {
      const gabcNotations = context.gabcExpression.map((notation: CstNode) =>
        this.visit(notation));

      let nabcNotations = [];

      if (context.nabcGallExpression) {
        nabcNotations = context.nabcGallExpression.map((notation: CstNode) =>
          this.visit(notation));
      }

      if (context.nabcLaonExpression) {
        nabcNotations = context.nabcLaonExpression.map((notation: CstNode) =>
          this.visit(notation));
      }

      return new GregorioSegment(flatten(gabcNotations), nabcNotations);
    }

    public nabcOnlySegment(context: any): GregorioSegment {
      let nabcNotations = [];

      if (context.nabcGallExpression) {
        nabcNotations = context.nabcGallExpression.map((notation: CstNode) =>
          this.visit(notation));
      }

      if (context.nabcLaonExpression) {
        nabcNotations = context.nabcLaonExpression.map((notation: CstNode) =>
          this.visit(notation));
      }

      return new GregorioSegment([], nabcNotations);
    }

    public gabcExpression(context: any): GregorioNoteElement[] {
      const notationsDict: { [key: string]: GregorioNoteElement } = {};
      let bar = null;

      let prevNote: GregorioNoteElement | undefined = undefined;

      const notations = Object.keys(context).map((key) => context[key]).flat();
      const orderedNotations = sortBy(notations, _ => (_.startOffset));
      const interpreter_context = new InterpreterContext();

      for (const notation of orderedNotations) {
        const text = notation.image;

        switch (notation.tokenType.name) {
          case 'WHITESPACE_SINGLE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.AD_HOC_SPACE);
            break;
          case 'B_VIRGULA_PAREN_HIGH':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.VIRGULA_PAREN_HIGH);
            break;
          case 'B_VIRGULA_PAREN':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSeparatinBar(GregorioBar.VIRGULA_PAREN);
            break;
          case 'B_VIRGULA_HIGH':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSeparatinBar(GregorioBar.VIRGULA_HIGH);
            break;
          case 'B_VIRGULA':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSeparatinBar(GregorioBar.VIRGULA);
            break;
          case 'B_DIVISIO_MINIMIS_HIGH':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMIS_HIGH);
            break;
          case 'B_DIVISIO_MINIMIS':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMIS);
            break;
          case 'B_DIVISIO_MINIMA_PAREN_HIGH':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMA_PAREN_HIGH);
            break;
          case 'B_DIVISIO_MINIMA_PAREN':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMA_PAREN);
            break;
          case 'B_DIVISIO_MINIMA_HIGH':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMA_HIGH);
            break;
          case 'B_DIVISIO_MINIMA':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINIMA);
            break;
          case 'B_DOMINICAN_BAR':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildDominicanBar(text[1]);
            break;
          case 'B_DIVISIO_MINOR':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MINOR);
            break;
          case 'B_DIVISIO_FINALIS':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_FINALIS);
            break;
          case 'B_DIVISIO_MAIOR':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MAIOR);
            break;
          case 'B_DIVISIO_MAIOR_DOTTED':
            notationsDict[notation.startOffset] =
              GregorioNoteElement.buildSeparatinBar(GregorioBar.DIVISIO_MAIOR_DOTTED);
            break;
          case 'SP_HALF_SPACE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.HALF_SPACE);
            break;
          case 'SP_INTERGLYPH_SPACE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.INTERGLYPH_SPACE);
            break;
          case 'SP_NEUMATIC_CUT':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.NEUMATIC_CUT);
            break;
          case 'SP_GLYPH_SPACE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.GLYPH_SPACE);
            break;
          case 'SP_LARGER_SPACE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.LARGER_SPACE);
            break;
          case 'SP_LARGER_SPACE_NB':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.LARGER_SPACE_NB);
            break;
          case 'SP_GLYPH_SPACE_NB':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.GLYPH_SPACE_NB);
            break;
          case 'SP_ZERO_WIDTH':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildSpace(GregorioSpace.ZERO_WIDTH);
            break;
          case 'S_VIRGA':
            prevNote?.changeShape(GregorioShape.VIRGA);
            break;
          case 'S_VIRGA_REVERSA':
            prevNote?.changeShape(GregorioShape.VIRGA_REVERSA);
            break;
          case 'S_QUILISMA':
            prevNote?.changeShape(GregorioShape.QUILISMA);
            break;
          case 'S_QUILISMA_QUADRATUM':
            prevNote?.changeShape(GregorioShape.QUILISMA_QUADRATUM);
            break;
          case 'S_STROPHA':
            prevNote?.changeShape(GregorioShape.STROPHA);
            break;
          case 'S_QUADRATUM':
            prevNote?.changeShape(GregorioShape.QUADRATUM);
            break;
          case 'S_ORISCUS_UNDETERMINED':
            prevNote?.changeShape(GregorioShape.ORISCUS_UNDETERMINED);
            break;
          case 'S_ORISCUS_DESCENDENS':
            prevNote?.changeShape(GregorioShape.ORISCUS_DESCENDENS);
            break;
          case 'S_ORISCUS_ASCENDENS':
            prevNote?.changeShape(GregorioShape.ORISCUS_ASCENDENS);
            break;
          case 'S_ORISCUS_SCAPUS_UNDETERMINED':
            prevNote?.changeShape(GregorioShape.ORISCUS_SCAPUS_UNDETERMINED);
            break;
          case 'S_ORISCUS_SCAPUS_DESCENDENS':
            prevNote?.changeShape(GregorioShape.ORISCUS_SCAPUS_DESCENDENS);
            break;
          case 'S_ORISCUS_SCAPUS_ASCENDENS':
            prevNote?.changeShape(GregorioShape.ORISCUS_SCAPUS_ASCENDENS);
            break;
          case 'S_FLAT':
            prevNote?.changeShape(GregorioShape.FLAT);
            break;
          case 'S_FLAT_PAREN':
            prevNote?.changeShape(GregorioShape.FLAT_PAREN);
            break;
          case 'S_SHARP':
            prevNote?.changeShape(GregorioShape.SHARP);
            break;
          case 'S_SHARP_PAREN':
            prevNote?.changeShape(GregorioShape.SHARP_PAREN);
            break;
          case 'S_NATURAL':
            prevNote?.changeShape(GregorioShape.NATURAL);
            break;
          case 'S_NATURAL_PAREN':
            prevNote?.changeShape(GregorioShape.NATURAL_PAREN);
            break;
          case 'S_LINEA':
            prevNote?.changeShape(GregorioShape.LINEA);
            break;
          case 'S_LINEA_PUNCTUM':
            prevNote?.changeShape(GregorioShape.LINEA_PUNCTUM);
            break;
          case 'CAVUM':
            prevNote?.set_cavum(true);
            break;
          case 'S_LINEA_PUNCTUM_CAVUM':
            prevNote?.changeShape(GregorioShape.LINEA_PUNCTUM);
            prevNote?.set_cavum(true);
            break;
          case 'TAIL_LIQUIESCENIA':
            prevNote?.addTailLiquescentia(GregorioLiquescentia.AUCTUS_ASCENDENS, false);
            break;
          case 'TAIL_LIQUIESCENIA_AUCTUS_DESCENDENS':
            prevNote?.addTailLiquescentia(GregorioLiquescentia.AUCTUS_DESCENDENS, false);
            break;
          case 'TAIL_LIQUIESCENIA_DEMINUTUS':
            prevNote?.addTailLiquescentia(GregorioLiquescentia.DEMINUTUS, false);
            break;
          case 'H_EPISEMA':
            if (prevNote) {
              const notationsArr = sortBy(Object.keys(notationsDict), (k) => parseInt(k)).map((k) => notationsDict[k]);
              const prevNoteIndex = notationsArr.indexOf(prevNote);
              addHorizontalEpisemaFromText(notationsArr, prevNoteIndex, text.slice(1) || '', interpreter_context);
            } else {
              bar = findBar(context);
              if (bar) {
                notationsDict[bar.startOffset]?.addHorizontalEpisemaForBar();
              }
            }
            break;
          case 'V_EPISEMA':
            if (prevNote) {
              prevNote.addSign(GregorioSign.V_EPISEMA, text);
            } else {
              bar = findBar(context);
              if (bar) {
                notationsDict[bar.startOffset].addSign(GregorioSign.V_EPISEMA, text);
              }
            }
            break;
          case 'PUNCTUM_MORA':
            prevNote?.addSign(GregorioSign.PUNCTUM_MORA, text);
            break;
          case 'END_OF_LINE':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(false, false, false);
            break;
          case 'END_OF_LINE_FORSES_CUSTOS':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(false, true, true);
            break;
          case 'END_OF_LINE_FORSES_CUSTOS_ON':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(false, true, false);
            break;
          case 'END_OF_LINE_RAGGED':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(true, false, false);
            break;
          case 'END_OF_LINE_RAGGED_CUSTOS':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(true, true, true);
            break;
          case 'END_OF_LINE_RAGGED_CUSTOS_ON':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildEndOfLine(true, true, false);
            break;
          case 'CUSTOS':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildCustos();
            break;
          case 'MANUAL_CUSTOS':
            notationsDict[notation.startOffset] = GregorioNoteElement.buildManualCustos(text);
            break;
          default:
            interpreter_context.nbof_isolated_episema = 0;
            const note = this.determineNoteElement(notation.tokenType.name, text);
            notationsDict[notation.startOffset] = note;
            prevNote = note;
        }
      }

      const arr = sortBy(Object.keys(notationsDict), (k) => parseInt(k)).map((k) => notationsDict[k]);
      return arr;
    }

    determineNoteElement(type: string, text: string): GregorioNoteElement {
      switch (type) {
        case 'CLEF':
          return GregorioNoteElement.buildClef(
            GregorioClefInfo.letter_to_clef(text[0]),
            GregorioClefInfo.parse_clef_line(text[1]),
            false);
        case 'CLEF_FLATTED':
          return GregorioNoteElement.buildClef(
            GregorioClefInfo.letter_to_clef(text[0]),
            GregorioClefInfo.parse_clef_line(text[2]),
            true);
        // case 'CLEF_SECONDARY':
        //   return GregorioNoteElement.buildClef(
        //     GregorioClefInfo.letter_to_clef(text[1]),
        //     GregorioClefInfo.parse_clef_line(text[2]),
        //     false);
        // case 'CLEF_SECONDARY_FLATTED':
        //   return GregorioNoteElement.buildClef(
        //     GregorioClefInfo.letter_to_clef(text[1]),
        //     GregorioClefInfo.parse_clef_line(text[3]),
        //     false);
        case 'NOTE_PUNCTUM':
          return GregorioNoteElement.buildNote(
            text,
            GregorioShape.PUNCTUM,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_PUNCTUM_INITIO_DEBILIS':
          return GregorioNoteElement.buildNote(
            text[1],
            GregorioShape.PUNCTUM,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.INITIO_DEBILIS
          );
        case 'NOTE_PUNCTUM_FUSED':
          return GregorioNoteElement.buildNote(
            text[1],
            GregorioShape.PUNCTUM,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.FUSED
          );
        case 'NOTE_PUNCTUM_INCLINATUM':
          return GregorioNoteElement.buildNote(
            text[0],
            punctumInclinatum(text[1]),
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_PUNCTUM_INCLINATUM_INITIO_DEBILIS':
          return GregorioNoteElement.buildNote(text[1],
            punctumInclinatum(text[2]),
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.INITIO_DEBILIS
          );
        case 'NOTE_PUNCTUM_INCLINATUM_FUSED':
          return GregorioNoteElement.buildNote(
            text[1],
            punctumInclinatum(text[2]),
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.FUSED
          );
        case 'NOTE_BIVIRGA':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.BIVIRGA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_TRIVIRGA':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.TRIVIRGA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_DISTROPHA':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.DISTROPHA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_DISTROPHA_AUCTUS_ASCENDENS':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.DISTROPHA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.AUCTUS_ASCENDENS
          );
        case 'NOTE_TRISTROPHA':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.TRISTROPHA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.NO_LIQUESCENTIA
          );
        case 'NOTE_TRISTROPHA_AUCTUS_ASCENDENS':
          return GregorioNoteElement.buildNote(
            text[0],
            GregorioShape.TRISTROPHA,
            GregorioSign.NO_SIGN,
            GregorioLiquescentia.AUCTUS_ASCENDENS
          );
        default:
          throw new Error(`Unknown note type: ${type}`);
      }
    }

    public nabcGallExpression(context: any) {
      return new NabcSnippet(
        context.nabcGallNeume.map((neume: any) => {
          return this.visit(neume);
        }),
        'gall',
      );
    }

    public nabcGallNeume(context: any) {
      return new NabcNeume(
        this.visit(context.nabcGallGlyphs),
        context.NABC_HORIZONTAL_SPACING_ADJUSTMENT && context.NABC_HORIZONTAL_SPACING_ADJUSTMENT[0].image,
        context.NABC_SIGNIFICANT_LETTER_GALL && context.NABC_SIGNIFICANT_LETTER_GALL.map((letter: any) =>
          new NabcSignificantLetter(letter.image)),
        context.NABC_SUBPUNCTIS_AND_PREPUNCTIS_GALL && context.NABC_SUBPUNCTIS_AND_PREPUNCTIS_GALL.map((letter: any) =>
          new NabcSubpunctisAndPrepunctis(letter.image)),
        // context.NABC_ERROR?.length,
      );
    }

    public nabcGallGlyphs(context: any) {
      return context.nabcGallGlyph?.map((neume: any) => this.visit(neume));
    }

    public nabcGallGlyph(context: any) {
      return new NabcGlyph(
        context.NABC_BASIC_GLYPH_GALL[0].image,
        context.NABC_PITCH && context.NABC_PITCH[0].image,
        context.NABC_GLYPH_MODIFIER && context.NABC_GLYPH_MODIFIER[0].image,
      );
    }

    public nabcLaonExpression(context: any) {
      return new NabcSnippet(
        context.nabcLaonNeume.map((neume: any) => {
          return this.visit(neume);
        }),
        'laon',
      );
    }

    public nabcLaonNeume(context: any) {
      const ls = [];
      if (context.NABC_SIGNIFICANT_LETTER_LAON) {
        ls.push(...context.NABC_SIGNIFICANT_LETTER_LAON.map((letter: any) =>
          new NabcSignificantLetter(letter.image)));
      }
      if (context.NABC_SIGNIFICANT_LETTER_TIRONIAN_LAON) {
        ls.push(...context.NABC_SIGNIFICANT_LETTER_TIRONIAN_LAON.map((letter: any) =>
          new NabcSignificantLetter(letter.image)));
      }

      return new NabcNeume(
        this.visit(context.nabcLaonGlyphs),
        context.NABC_HORIZONTAL_SPACING_ADJUSTMENT && context.NABC_HORIZONTAL_SPACING_ADJUSTMENT[0].image,
        ls,
        context.NABC_SUBPUNCTIS_AND_PREPUNCTIS_LAON && context.NABC_SUBPUNCTIS_AND_PREPUNCTIS_LAON.map((letter: any) =>
          new NabcSubpunctisAndPrepunctis(letter.image)),
        // context.NABC_ERROR?.length,
      );
    }

    public nabcLaonGlyphs(context: any) {
      return context.nabcLaonGlyph.map((neume: any) => this.visit(neume));
    }

    public nabcLaonGlyph(context: any) {
      return new NabcGlyph(
        context.NABC_BASIC_GLYPH_LAON[0].image,
        context.NABC_PITCH && context.NABC_PITCH[0].image,
        context.NABC_GLYPH_MODIFIER && context.NABC_GLYPH_MODIFIER[0].image,
      );
    }
  }

  return new CompoundedInterpreter();
}


const commentsPreprocessor = (input: string) => input.split('\n').filter((line) => !line.startsWith('%')).join('\n');

const gnabcInputPreprocessors: ((input: string) => string)[] = [
  commentsPreprocessor,
  (input) => input.trim(),
  (input) => input.replace(/(\r\n|\n|\r)/gm, ''),
  (input) => {
    const numOfOpens = input.match(/\(/g)?.length || 0;
    const numOfCloses = input.match(/\)/g)?.length || 0;
    if (numOfOpens - numOfCloses === 1) {
      return input + ')';
    }
    return input;
  },
  (input) => input.replace(/([^\s()])\s+\(/g, '$1('),
  (input) => input.replace(/\[[^\]]*\]/g, ''), // Remove anything between square brackets and the brackets themselves
  (input) => { // remove repeated spaces if not inside parentheses
    let isInsideParentheses = false;
    let output = '';
    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      if (char === '(') {
        isInsideParentheses = true;
      } else if (char === ')') {
        isInsideParentheses = false;
      }
      if (char === ' ' && !isInsideParentheses) {
        if (input[i + 1] === ' ') {
          continue;
        }
      }
      output += char;
    }
    return output;
  },
];

function gnabcInputPreprocessing(text: string): string {
  return gnabcInputPreprocessors.reduce((acc, preprocessor) => preprocessor(acc), text);
}


const metatagsInputPreprocessors: ((input: string) => string)[] = [
  commentsPreprocessor,
];

function metatagsInputPreprocessing(text: string): string {
  return metatagsInputPreprocessors.reduce((acc, preprocessor) => preprocessor(acc), text);
}

function runPreprocessing(text: string): {
  gnabc: string,
  metadata: string | undefined,
} {
  let [metadataStr, gnabc] = split_metadata(text);
  if (metadataStr) {
    metadataStr = metatagsInputPreprocessing(metadataStr);
  }

  gnabc = gnabcInputPreprocessing(gnabc);

  return {
    gnabc: gnabc,
    metadata: metadataStr,
  };
}

function interpreteScore(text: string, lexer: Lexer, parser: CompoundedParser, interpreter: any): {
  syllables: GregorioSyllable[],
  lexResult: IToken[],
  lexErrors: ILexingError[],
  parseErrors: IRecognitionException[],
  metadata: GregorioMetadata,
  gnabc: string,
} {
  const result = runPreprocessing(text);
  const lexerResult = lexer.tokenize(result.gnabc);

  parser.input = lexerResult.tokens;
  const cst = parser.parse();
  const syllables = interpreter.visit(cst);

  return {
    syllables: syllables,
    lexResult: lexerResult.tokens,
    lexErrors: lexerResult.errors,
    parseErrors: parser.errors,
    metadata: new GregorioMetadata(result.metadata),
    gnabc: result.gnabc,
  };
}


export { runPreprocessing, interpreteScore };
